import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Ellipsis } from '@openx/components-core/lib/Ellipsis/Ellipsis.tsx';
import type { AudienceSegmentEntry, InventoryBundle, InventoryDomain } from '@openx/types/gqlTypes.ts';
import type { BaseRow, TableColumn } from '@openx/types/tableHelpers.ts';
import type { TFunction } from 'i18next';
import { type JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDealPrompt } from '../../../hooks/useDealPrompt.ts';
import { dealPromptContextTypes } from '../../../utils/types.ts';
import { NameCell } from './NameCell.tsx';
import { NumericCell } from './NumericCell.tsx';

const renderNumericCellHandler = (value: number, cell: JSX.Element, fieldLabel: string) => {
  const { t } = useTranslation();
  return value ? (
    cell
  ) : (
    <Tooltip
      title={t('{fieldLabel} cannot currently be estimated for this segment due to data limitations.', {
        fieldLabel,
      })}
    >
      <Typography
        sx={{
          fontWeight: 'fontWeightRegular',
          color: 'text.disabled',
        }}
      >
        {t('N/A')}
      </Typography>
    </Tooltip>
  );
};

const getColumnsPerContext = (t: TFunction) => ({
  [dealPromptContextTypes.DOMAINS]: [
    {
      key: 'domainName',
      title: t('Domain Items'),
      disabled: true,
      render: (row: InventoryDomain) => <NameCell name={row.domainName || ''} isDirect={row.isDirect} />,
      width: '22%',
    },
  ],
  [dealPromptContextTypes.APPS]: [
    {
      key: 'bundleId',
      title: t('Application Bundle ID'),
      disabled: true,
      render: (row: InventoryBundle) => <NameCell name={row.bundleId || ''} isDirect={row.isDirect} />,
      minWidth: '400px',
      sticky: 'left',
    },
    {
      key: 'platform',
      title: t('Platform'),
      disabled: true,
      render: (row: InventoryBundle) => <Typography variant="body1Bold">{row.platform || ''}</Typography>,
      width: '7%',
      minWidth: '100px',
    },
  ],
  [dealPromptContextTypes.AUDIENCE]: [
    {
      key: 'name',
      title: t('Segment Name'),
      disabled: true,
      render: (row: AudienceSegmentEntry) => (
        <Typography variant="body1Bold" data-test="row-title">
          <Ellipsis width="100%" tooltip tooltipMessage={row.name} tooltipPlacement="bottom-end">
            {row.name}
          </Ellipsis>
        </Typography>
      ),
      width: '18%',
      align: 'left',
    },
    {
      key: 'description',
      title: t('Description'),
      disabled: true,
      render: (row: AudienceSegmentEntry) => (
        <Typography variant="body1Bold">
          <Ellipsis width="100%" tooltip tooltipMessage={row.description} tooltipPlacement="bottom-end">
            {row.description}
          </Ellipsis>
        </Typography>
      ),
      width: '8%',
      align: 'left',
    },
    {
      key: 'category',
      title: t('Category'),
      disabled: true,
      render: (row: AudienceSegmentEntry) => (
        <Typography variant="body1Bold">
          <Ellipsis width="100%" tooltip tooltipMessage={row.segment_category} tooltipPlacement="bottom-end">
            {row.segment_category}
          </Ellipsis>
        </Typography>
      ),
      width: '6%',
      align: 'left',
    },
    {
      key: 'account_name',
      title: t('Data Partner'),
      disabled: true,
      render: (row: AudienceSegmentEntry) => <Typography variant="body1Bold">{row.account_name}</Typography>,
      width: '6%',
      align: 'left',
    },
    {
      key: 'price_cpm',
      title: t('Price'),
      disabled: true,
      render: (row: AudienceSegmentEntry) => {
        const hasPom = row.price_pom != null;
        const hasCpm = row.price_cpm != null;
        if (!hasPom && !hasCpm) return null;
        return (
          <Typography component="span">
            {hasPom && <NumericCell value={row.price_pom * 100} suffix="%" decimalPlaces={2} />}
            {hasPom && hasCpm && ' / '}
            {hasCpm && <NumericCell value={row.price_cpm} prefix="$" decimalPlaces={2} trailingZeroes />}
          </Typography>
        );
      },
      width: '5%',
      minWidth: '120px',
      align: 'left',
    },
    {
      key: 'reach',
      title: t('Unique Users'),
      disabled: true,
      render: (row: AudienceSegmentEntry) => <NumericCell value={row.reach} />,
      width: '8%',
      align: 'left',
    },
    {
      key: 'devicesTV',
      title: t('CTV Devices'),
      disabled: true,
      render: (row: AudienceSegmentEntry) =>
        renderNumericCellHandler(row.devicesTV, <NumericCell value={row.devicesTV} decimalPlaces={1} />, 'CTV Devices'),
      width: '4%',
      align: 'left',
    },
    {
      key: 'activeIps',
      title: t('Active IPs'),
      disabled: true,
      render: (row: AudienceSegmentEntry) =>
        renderNumericCellHandler(row.activeIPs, <NumericCell value={row.activeIPs} />, 'Active Ips'),
      width: '4%',
      align: 'left',
    },
    {
      key: 'mobileDevices',
      title: t('Mobile Devices'),
      disabled: true,
      render: (row: AudienceSegmentEntry) =>
        renderNumericCellHandler(
          row.devicesMobile,
          <NumericCell value={row.devicesMobile} decimalPlaces={1} />,
          'Mobile Devices'
        ),
      width: '4%',
      align: 'left',
    },
  ],
});

const getAppsAndDomainsCommonColumns = (t: TFunction) => [
  {
    key: 'totalBidSpendInUsd',
    title: t('30d Spend'),
    align: 'left',
    render: ({ totalBidSpendInUsd }: InventoryDomain | InventoryBundle) => (
      <NumericCell value={Number(totalBidSpendInUsd)} prefix="$" decimalPlaces={2} />
    ),
    width: '10%',
    minWidth: '120px',
  },
  {
    key: 'totalCprMarketRequests',
    title: t('Market Requests'),
    align: 'left',
    render: ({ totalCprMarketRequests }: InventoryDomain | InventoryBundle) => (
      <NumericCell value={totalCprMarketRequests} decimalPlaces={2} />
    ),
    width: '10%',
    minWidth: '160px',
  },
  {
    key: 'relevance',
    title: t('Relevance'),
    align: 'left',
    render: ({ relevance }: InventoryDomain | InventoryBundle) => <NumericCell value={relevance} suffix="%" />,
    width: '8%',
    minWidth: '120px',
  },
  {
    key: 'totalImpressions',
    title: t('Impressions'),
    align: 'left',
    render: ({ totalImpressions }: InventoryDomain | InventoryBundle) => (
      <NumericCell value={totalImpressions} decimalPlaces={2} />
    ),
    width: '8%',
    minWidth: '160px',
  },
  {
    key: 'avgBidCpm',
    title: t('Avg CPM'),
    align: 'left',
    render: ({ avgBidCpm }: InventoryDomain | InventoryBundle) => (
      <NumericCell value={avgBidCpm} prefix="$" decimalPlaces={2} />
    ),
    width: '8%',
    minWidth: '100px',
  },
  {
    key: 'avgViewability',
    title: t('Viewability'),
    align: 'left',
    render: ({ avgViewability }: InventoryDomain | InventoryBundle) => (
      <NumericCell value={avgViewability} suffix="%" decimalPlaces={2} />
    ),
    width: '9%',
    minWidth: '120px',
  },
  {
    key: 'avgPubWinRate',
    title: t('Bid Rate'),
    align: 'left',
    render: ({ avgPubWinRate }: InventoryDomain | InventoryBundle) => (
      <NumericCell value={avgPubWinRate} suffix="%" decimalPlaces={2} />
    ),
    width: '8%',
    minWidth: '100px',
  },
  {
    key: 'avgOxWinRate',
    title: t('Win Rate'),
    align: 'left',
    render: ({ avgOxWinRate }: InventoryDomain | InventoryBundle) => (
      <NumericCell value={avgOxWinRate} suffix="%" decimalPlaces={2} />
    ),
    width: '10%',
    minWidth: '120px',
  },
];

export const useDealPromptDetailsTableConfig = () => {
  const { t } = useTranslation();
  const {
    state: { contextType },
  } = useDealPrompt();

  const config: TableColumn<InventoryDomain | InventoryBundle>[] = useMemo(() => {
    return [
      ...(getColumnsPerContext(t)[contextType] ?? []),
      ...(contextType === dealPromptContextTypes.AUDIENCE ? [] : getAppsAndDomainsCommonColumns(t)),
    ];
  }, [t, contextType]);

  return config as TableColumn<BaseRow>[];
};
